// The season, a row a week, from one team's side (B-386): when, against whom
// and how it went, or the dates while it is to come. A row opens the week's
// games as the scoreboard's tiles, that team's first.

import type { ScheduleView } from '@fsp/domain';
import { useState } from 'react';
import { useAtLeast } from '../../data/detail';
import { useReaderZone } from '../../data/hooks';
import { LiveDot, TeamMark } from '../../kit';
import { Icon } from '../../kit/Icon';
import { Panel } from '../../kit/Panel';
import { Chip } from '../../kit/ui';
import { GameGrid } from '../scoreboard/GameGrid';
import { begun, teamsIn as fieldTeams, weekRecord } from '../gameDay/FieldTable';
import { ordinal } from '../shared/RosterMoveSheet';
import { averagesOf, recordText, weekDates, weekKey, weeksOf, type Week } from './season';

export function SeasonWeeks({
  view,
  teamId,
  allPlay,
}: {
  view: ScheduleView;
  /** Whose season: mine, or the team picked. */
  teamId: string;
  allPlay: boolean;
}) {
  const zone = useReaderZone();
  const weeks = view.periods.flatMap((p) => weeksOf(p, teamId));
  const averages = averagesOf(view);
  // EVERY WEEK SHUT ON ARRIVAL: the week under way is the panel above, and
  // the rest are a tap each.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (i: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  return (
    <Panel title="Season">
      <ol className="fsp-sc-weeks">
        {weeks.map((w) => (
          <WeekRow
            key={weekKey(w)}
            w={w}
            teamId={teamId}
            mine={teamId === view.myTeamId}
            open={open.has(weekKey(w))}
            onToggle={() => toggle(weekKey(w))}
            zone={zone}
            average={w.them ? (averages.get(w.them.team.id) ?? null) : null}
            allPlay={allPlay}
          />
        ))}
      </ol>
    </Panel>
  );
}

function WeekRow({
  w,
  teamId,
  mine,
  open,
  onToggle,
  zone,
  average,
  allPlay,
}: {
  w: Week;
  teamId: string;
  /** The season on screen is mine: a week against the field is mine alone to read. */
  mine: boolean;
  open: boolean;
  onToggle: () => void;
  zone: string | null;
  /** The opponent's average score a week so far, for a week to come. */
  average: number | null;
  allPlay: boolean;
}) {
  const standard = useAtLeast('standard');
  const pro = useAtLeast('pro');
  const { p } = w;
  const opp = w.them?.team ?? null;
  // MY WEEK AGAINST THE FIELD (Q4, Q5): my score and place once it has
  // begun, and all-play's week record from Standard up.
  const field = mine ? p.myField : null;
  // A WEEK'S SECOND GAME (Q6) says so where the first says its dates.
  const label = w.second
    ? 'Second game'
    : p.period.isCurrent
      ? 'This week'
      : weekDates(p.period.start, p.period.end, zone);
  return (
    <li
      className="fsp-sc-week"
      data-outcome={w.outcome ?? undefined}
      data-current={p.period.isCurrent || undefined}
    >
      <div
        className="fsp-row fsp-row-clickable fsp-sc-row"
        onClick={onToggle}
        // A button answers Enter and Space as well as a click (UX-30).
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          onToggle();
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <span className="fsp-sc-when">
          <span className="fsp-sc-label">{p.period.label}</span>
          <span className="fsp-sc-date">{label}</span>
        </span>
        <span className="fsp-sc-opp">
          <span className="fsp-sc-opp-line">
            {opp ? (
              <>
                <span className="fsp-sc-vs">{w.home ? 'vs' : 'at'}</span>
                <TeamMark team={opp} size={20} />
                <span className="fsp-sc-opp-name">{opp.name}</span>
              </>
            ) : (
              <span className="fsp-muted">
                {field
                  ? 'Against the whole league'
                  : w.rest
                    ? 'Sits out this game'
                    : w.bye
                      ? 'Bye week'
                      : p.kind === 'regular'
                        ? 'No games'
                        : p.matchups.length
                          ? 'No game this round'
                          : 'Bracket to be decided'}
              </span>
            )}
            {p.kind !== 'regular' ? (
              <Chip tone={p.kind === 'playoff' ? 'warn' : 'neutral'}>
                {p.kind === 'playoff' ? 'Playoffs' : 'Consolation'}
              </Chip>
            ) : null}
            {p.rivalry ? <Chip tone="bad">Rivalry</Chip> : null}
          </span>
          {/* WHO THEY ARE NOW, from Standard: the opponent's record and
              manager, and for a week to come how many it scores a week. */}
          {opp && standard ? (
            <span className="fsp-sc-opp-sub">
              {recordText(opp.record)} · {opp.ownerName}
              {w.outcome === 'pre' && average !== null ? (
                <>
                  {' '}
                  · <span className="fsp-num">{average.toFixed(1)}</span> a week
                </>
              ) : null}
            </span>
          ) : null}
        </span>
        <span className="fsp-sc-result">
          <Result w={w} standard={standard} pro={pro} teamId={teamId} />
          {field && begun(field, fieldTeams(field)) ? (
            <span className="fsp-num">
              {field.score.toFixed(1)} · {ordinal(field.place)}
              {allPlay && standard ? ` · ${weekRecord(field)}` : ''}
            </span>
          ) : null}
        </span>
        <span className="fsp-sc-chev fsp-muted" aria-hidden>
          <Icon name={open ? 'expand' : 'next'} />
        </span>
      </div>
      {open ? (
        p.matchups.length ? (
          <div className="fsp-sc-games">
            <GameGrid games={p.matchups} stats={p.stats} first={teamId} />
          </div>
        ) : (
          <div className="fsp-sub fsp-ui-wrap-text fsp-sc-empty">
            {field
              ? 'Every team plays the whole league this week; the scoreboard has the order.'
              : p.kind === 'regular'
                ? 'Nothing scheduled.'
                : 'The bracket fills in as the regular season ends.'}
          </div>
        )
      ) : null}
    </li>
  );
}

/** How the week went: W, L or T, and from Standard the score; or where it stands. */
function Result({
  w,
  standard,
  pro,
  teamId,
}: {
  w: Week;
  standard: boolean;
  pro: boolean;
  teamId: string;
}) {
  if (w.game === null || w.us === null || w.them === null || w.outcome === null) return null;
  const score = `${w.us.score.toFixed(1)}–${w.them.score.toFixed(1)}`;
  if (w.outcome === 'pre') {
    // THE CHANCE, at Everything, where somebody has worked one out: this
    // team's, so it never needs a name (TODO(H2): the schedule serves no
    // projections yet, so a week to come has none).
    const p = w.game.winProbabilityHome;
    if (!pro || p === null) return null;
    const ours = w.game.home.team.id === teamId ? p : 1 - p;
    return (
      <span className="fsp-sc-chance">
        <span className="fsp-num">{Math.round(ours * 100)}%</span> to win
      </span>
    );
  }
  if (w.outcome === 'live') {
    return (
      <>
        {/* Live only while the numbers are (B-171): a week whose lines land
            overnight is in progress. */}
        {w.p.stats.state === 'live' ? (
          <LiveDot label="Live" />
        ) : (
          <span className="fsp-sc-progress">In progress</span>
        )}
        {standard ? <span className="fsp-num fsp-sc-score">{score}</span> : null}
      </>
    );
  }
  return (
    <>
      {/* THE RESULT IN A LETTER as well as a color (UX-28). */}
      <span className="fsp-sc-mark" data-outcome={w.outcome}>
        {w.outcome === 'win' ? 'W' : w.outcome === 'loss' ? 'L' : 'T'}
      </span>
      {standard ? <span className="fsp-num fsp-sc-score">{score}</span> : null}
    </>
  );
}
